import React, { useState, useEffect } from 'react';
import { 
  FiSettings, FiX, FiSearch, FiCalendar, FiBarChart2, 
  FiMessageSquare, FiBell, FiMoon, FiSun, 
  FiGlobe, FiDatabase, FiShield, FiTrash2, FiDownload, FiArrowLeft
} from 'react-icons/fi';
import './MiniDashboard.css';
import { useNavigate } from 'react-router-dom';

export default function MiniDashboard() {
  const [open, setOpen] = useState(false);
  const [activeView, setActiveView] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [language, setLanguage] = useState('en');
  const [privacyMode, setPrivacyMode] = useState(false);
  const [dataSaver, setDataSaver] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Initialize dark mode and preferences
  useEffect(() => {
    try {
      const savedDarkMode = localStorage.getItem('darkMode') === 'true';
      const systemDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const initialDarkMode = savedDarkMode !== null ? savedDarkMode : systemDarkMode;

      setDarkMode(initialDarkMode);
      document.body.classList.toggle('dark-mode', initialDarkMode);

      const savedNotifications = localStorage.getItem('notifications');
      if (savedNotifications !== null) {
        setNotificationsEnabled(savedNotifications === 'true');
      }

      const savedLanguage = localStorage.getItem('language');
      if (savedLanguage) setLanguage(savedLanguage);

      const savedPrivacyMode = localStorage.getItem('privacyMode');
      if (savedPrivacyMode !== null) {
        setPrivacyMode(savedPrivacyMode === 'true');
      }

      const savedDataSaver = localStorage.getItem('dataSaver');
      if (savedDataSaver !== null) {
        setDataSaver(savedDataSaver === 'true');
      }
    } catch (e) {
      console.warn('LocalStorage not available', e);
    }
  }, []);

  const handleAction = (action) => {
    setActiveView(action);
    if (!['settings', 'privacy', 'data', 'search'].includes(action)) {
      setOpen(false);
    }
  };

  const handleNavigation = (path) => {
    setOpen(false);
    setTimeout(() => navigate(path), 150);
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    document.body.classList.toggle('dark-mode', newDarkMode);
    try {
      localStorage.setItem('darkMode', newDarkMode);
    } catch {}
  };

  const toggleNotifications = () => {
    const newEnabled = !notificationsEnabled;
    setNotificationsEnabled(newEnabled);
    try {
      localStorage.setItem('notifications', newEnabled);
    } catch {}

    if (newEnabled && 'Notification' in window && Notification.permission === 'granted') {
      new Notification('Notifications Enabled', {
        body: 'You will now receive notifications from the app.'
      });
    }
  };

  const togglePrivacyMode = () => {
    const newPrivacyMode = !privacyMode;
    setPrivacyMode(newPrivacyMode);
    try {
      localStorage.setItem('privacyMode', newPrivacyMode);
    } catch {}
  };

  const toggleDataSaver = () => {
    const newDataSaver = !dataSaver;
    setDataSaver(newDataSaver);
    try {
      localStorage.setItem('dataSaver', newDataSaver);
    } catch {}
  };

  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    setLanguage(newLang);
    try {
      localStorage.setItem('language', newLang);
    } catch {}
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      alert(`Searching for: ${searchQuery}`);
      setSearchQuery('');
      setOpen(false);
    }
  };

  const clearBrowserData = () => {
    if (window.confirm('Are you sure you want to clear all browsing data?')) {
      try {
        localStorage.removeItem('searchHistory');
        sessionStorage.clear();
      } catch {}
      alert('Browser data cleared!');
    }
  };

  const exportData = () => {
    const data = {
      userPreferences: { darkMode, notifications: notificationsEnabled, language, privacyMode, dataSaver },
      exportDate: new Date().toISOString()
    };

    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = `user-data-export-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    alert('Data exported!');
  };

  const requestNotificationPermission = () => {
    if ('Notification' in window) {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          setNotificationsEnabled(true);
          try {
            localStorage.setItem('notifications', 'true');
          } catch {}
          new Notification('Notifications Enabled', { body: 'You will now receive notifications.' });
        }
      });
    }
  };

  const menuItems = [
    { icon: <FiCalendar />, label: 'Events Overview', action: () => handleNavigation('/events') },
    { icon: <FiBarChart2 />, label: 'Analytics', action: () => { handleAction('analytics'); window.open('https://supabase.com', '_blank'); }},
    { icon: <FiMessageSquare />, label: 'Support', action: () => { handleAction('support'); window.open('mailto:shivangpatel2050@gmail.com', '_blank'); }},
  ];

  return (
    <>
      <button
        className={`mini-dashboard-button ${open ? 'open' : ''}`}
        onClick={() => { setOpen(!open); if (!open) setActiveView(null); }}
        aria-label={open ? 'Close dashboard' : 'Open dashboard'}
      >
        {open ? <FiX size={20} /> : <FiSettings size={20} />}
      </button>

      {open && <div className="mini-dashboard-overlay" onClick={() => { setOpen(false); setActiveView(null); }} />}

      <div className={`mini-dashboard-panel ${open ? 'open' : ''}`}>
        <div className="panel-header">
          <h3>
            {activeView === 'settings' && 'Settings'}
            {activeView === 'privacy' && 'Privacy'}
            {activeView === 'data' && 'Data Management'}
            {!activeView && 'Quick Actions'}
            {activeView === 'search' && 'Search'}
          </h3>
        </div>

        {/* Search View */}
        {activeView === 'search' && (
          <div className="search-view">
            <div className="search-input-container">
              <FiSearch className="search-icon" />
              <input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyPress={handleSearch} autoFocus className="search-input" />
            </div>
          </div>
        )}

        {/* Main Menu */}
        {!activeView && (
          <div className="panel-content">
            <ul>
              {menuItems.map((item, idx) => (
                <li key={idx} onClick={item.action}>
                  <span className="icon">{item.icon}</span>
                  <span className="label">{item.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Settings Panel */}
        {activeView === 'settings' && (
          <div className="panel-content">
            <div className="settings-content">
              <div className="setting-item">
                <label htmlFor="darkModeToggle">
                  <span className="setting-icon">{darkMode ? <FiSun /> : <FiMoon />}</span> Dark Mode
                </label>
                <div className="toggle-switch">
                  <input type="checkbox" id="darkModeToggle" checked={darkMode} onChange={toggleDarkMode} />
                  <span className="slider"></span>
                </div>
              </div>

              <div className="setting-item">
                <label htmlFor="notificationsToggle">
                  <span className="setting-icon"><FiBell /></span> Notifications
                </label>
                <div className="toggle-switch">
                  <input type="checkbox" id="notificationsToggle" checked={notificationsEnabled} onChange={toggleNotifications} disabled={('Notification' in window && Notification.permission === 'denied')} />
                  <span className="slider"></span>
                </div>
              </div>

              {('Notification' in window && Notification.permission === 'default') && (
                <button className="permission-button" onClick={requestNotificationPermission}>
                  Grant Notification Permission
                </button>
              )}

              <div className="setting-item">
                <label htmlFor="privacyToggle"><span className="setting-icon"><FiShield /></span> Privacy Mode</label>
                <div className="toggle-switch">
                  <input type="checkbox" id="privacyToggle" checked={privacyMode} onChange={togglePrivacyMode} />
                  <span className="slider"></span>
                </div>
              </div>

              <div className="setting-item">
                <label htmlFor="dataSaverToggle"><span className="setting-icon"><FiDatabase /></span> Data Saver</label>
                <div className="toggle-switch">
                  <input type="checkbox" id="dataSaverToggle" checked={dataSaver} onChange={toggleDataSaver} />
                  <span className="slider"></span>
                </div>
              </div>

              <div className="setting-item">
                <label htmlFor="languageSelect"><span className="setting-icon"><FiGlobe /></span> Language</label>
                <select id="languageSelect" value={language} onChange={handleLanguageChange} className="language-select">
                  <option value="en">English</option>
                  <option value="es">Español</option>
                  <option value="fr">Français</option>
                  <option value="de">Deutsch</option>
                  <option value="ja">日本語</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Privacy Panel */}
        {activeView === 'privacy' && (
          <div className="panel-content">
            <div className="settings-content">
              <h4 className="sub-header">Privacy Settings</h4>
              <div className="setting-item">
                <label htmlFor="privacyToggle">Enhanced Privacy Mode <span className="setting-description">Restrict data collection</span></label>
                <div className="toggle-switch">
                  <input type="checkbox" id="privacyToggle" checked={privacyMode} onChange={togglePrivacyMode} />
                  <span className="slider"></span>
                </div>
              </div>
              <div className="setting-item">
                <label><FiTrash2 className="setting-icon" /> Clear Browsing Data</label>
                <button className="action-button" onClick={clearBrowserData}>Clear Now</button>
              </div>
            </div>
          </div>
        )}

        {/* Data Management Panel */}
        {activeView === 'data' && (
          <div className="panel-content">
            <div className="settings-content">
              <h4 className="sub-header">Data Management</h4>
              <div className="setting-item">
                <label htmlFor="dataSaverToggle">Data Saver Mode <span className="setting-description">Reduce data usage</span></label>
                <div className="toggle-switch">
                  <input type="checkbox" id="dataSaverToggle" checked={dataSaver} onChange={toggleDataSaver} />
                  <span className="slider"></span>
                </div>
              </div>
              
              <div className="setting-item">
                <label><FiDownload className="setting-icon" /> Export Data</label>
                <button className="action-button" onClick={exportData}>Export</button>
              </div>
            </div>
          </div>
        )}

        {(activeView === 'settings' || activeView === 'privacy' || activeView === 'data' || activeView === 'search') && (
          <div className="panel-footer">
            <button className="back-button" onClick={() => setActiveView(null)}>
              <FiArrowLeft style={{marginRight: '3px'}} /> Back
            </button>
          </div>
        )}
      </div>
    </>
  );
}
